import { Suspense, lazy, useState } from 'react';
import { NavResetContext, useNavReset } from '../../components/nav-reset.js';
import { Loading, SectionNav } from '../../components/ui.js';
import { ImportWizard } from '../import/wizard.js';
import { ProductsScreen } from '../products/products-screen.js';
import { Backorders } from '../purchasing/backorders-screen.js';
import { PurchaseOrders } from '../purchasing/receiving-screen.js';
import { VendorsScreen } from '../purchasing/vendors-screen.js';
import { CountsScreen } from './counts-screen.js';

/*
 * Loaded when its tab is first opened, like Reports: the register's first load
 * has a budget (scripts/check-bundle-budget.mjs), and the Categories screen's
 * order and web shop place (decision 223) took it 1.8 KiB past. A till ringing
 * a sale never needs it.
 */
const CategoriesScreen = lazy(() =>
  import('../products/categories-screen.js').then((module) => ({
    default: module.CategoriesScreen,
  })),
);

/**
 * Stock — everything about what the shop owns and what is on its way.
 *
 * **This section exists because the top bar ran out.** §2.2-2 caps a decision
 * point at seven and a manager saw exactly seven tabs: Register, Sales,
 * Customers, Products, Import, Receiving, Reports. End of day was folded into
 * Reports to make room once, and that trick does not work twice — so the next
 * thing to grow had to *group*. Products, Import and Purchase orders are all
 * one job (what we own, what we ordered, what arrived), and grouping them takes
 * the top level from seven choices to five while giving purchase-order
 * creation, backorders and the receiving document somewhere to live.
 *
 * The sub-nav is the same primitive Reports uses, so the shape of "a section
 * with parts" is learned once (§2.2-4).
 *
 * **Entries are filtered by permission, never disabled** (§2.2-7). An associate
 * holds `receiving.receive` — receiving is stockroom work, not a management
 * decision — so they get Products, Purchase orders and Backorders. Import and
 * *creating* an order need `purchasing.create`, which stops at manager.
 */

export type StockTab =
  'products' | 'categories' | 'purchaseOrders' | 'backorders' | 'vendors' | 'import' | 'counts';

export function StockSection({
  canManageCatalog,
  canPurchase,
  canReconcile = false,
  canReceive,
  onOpenReport,
  tab: requested,
  onTab,
}: {
  /**
   * The tab in the address (FB-1122): a tab press, help's Take me there
   * (FB-1083) and the browser's Back all move it the same way.
   */
  tab: string | null;
  onTab: (tab: StockTab) => void;
  canManageCatalog: boolean;
  canPurchase: boolean;
  /** Posting a return to vendor (decision 181). */
  canReconcile?: boolean;
  canReceive: boolean;
  /** Present only for somebody who may read the reports it opens. */
  onOpenReport?: (tab: string) => void;
}) {
  const items = [
    { key: 'products' as const, label: 'Products' },
    ...(canManageCatalog ? [{ key: 'categories' as const, label: 'Categories' }] : []),
    ...(canReceive || canPurchase
      ? [{ key: 'purchaseOrders' as const, label: 'Purchase orders' }]
      : []),
    ...(canReceive || canPurchase ? [{ key: 'backorders' as const, label: 'Backorders' }] : []),
    // Where a vendor's address and phone live (item 25) — a buying concern.
    ...(canPurchase ? [{ key: 'vendors' as const, label: 'Vendors' }] : []),
    ...(canPurchase ? [{ key: 'import' as const, label: 'Import' }] : []),
    // Counting is stockroom work (decision 124): everyone who can see stock can
    // count; posting asks for a manager inline. The seventh and last entry
    // this row can hold (§2.2-2).
    { key: 'counts' as const, label: 'Counts' },
  ];

  // Smart default (§2.2-5): looking a product up is what this section is opened
  // for most often, and it is the one part everybody can reach. A tab this
  // person cannot open, or a mistyped one, lands there too.
  const tab: StockTab = (requested as StockTab | null) ?? 'products';
  const setTab = onTab;
  /** Set when the import's "Open PO-…" jumps here (item 29). */
  const [expandOrderId, setExpandOrderId] = useState<string | null>(null);
  /** Per-tab reset counters — the sub-nav's half of item 31. */
  const [tabResets, setTabResets] = useState<Partial<Record<StockTab, number>>>({});
  const current = items.some((item) => item.key === tab) ? tab : 'products';

  const bump = (key: StockTab) =>
    setTabResets((counters) => ({ ...counters, [key]: (counters[key] ?? 0) + 1 }));

  // Re-pressing Stock in the top bar lands on the section's start: the
  // Products tab, itself reset. Other tabs keep their work — a half-mapped
  // import survives, exactly as it survives ordinary tab switches (§2.2-9).
  useNavReset(() => {
    setTab('products');
    bump('products');
  });

  const nav = (
    <SectionNav
      label="Inventory"
      current={current}
      items={items}
      onChange={(key) => {
        // The same rule one level down (item 31): pressing the sub-tab you
        // are on takes that screen back to its own start.
        if (key === current) bump(key);
        else setTab(key);
      }}
    />
  );
  const resetOf = (of: StockTab) => tabResets[of] ?? 0;

  return (
    <>
      {/*
        Products and Import stay mounted once visited, the way the register and
        the wizard always have: a half-mapped import exists only in this browser,
        and losing it to a tab press is losing real work (§2.2-9).
      */}
      <div hidden={current !== 'products'}>
        <NavResetContext.Provider value={resetOf('products')}>
          <ProductsScreen canManage={canManageCatalog} nav={nav} />
        </NavResetContext.Provider>
      </div>
      {canPurchase ? (
        <div hidden={current !== 'import'}>
          <ImportWizard
            nav={nav}
            onOpenOrder={(purchaseOrderId) => {
              setExpandOrderId(purchaseOrderId);
              setTab('purchaseOrders');
            }}
          />
        </div>
      ) : null}
      {current === 'categories' && canManageCatalog ? (
        <Suspense fallback={<Loading label="Opening Categories" />}>
          <CategoriesScreen canManage={canManageCatalog} nav={nav} />
        </Suspense>
      ) : null}
      {current === 'purchaseOrders' && (canReceive || canPurchase) ? (
        <NavResetContext.Provider value={resetOf('purchaseOrders')}>
          <PurchaseOrders
            nav={nav}
            canPurchase={canPurchase}
            canReconcile={canReconcile}
            initialExpanded={expandOrderId}
            {...(onOpenReport === undefined ? {} : { onSeeReorder: () => onOpenReport('reorder') })}
          />
        </NavResetContext.Provider>
      ) : null}
      {current === 'backorders' && (canReceive || canPurchase) ? <Backorders nav={nav} /> : null}
      {current === 'counts' ? (
        <NavResetContext.Provider value={resetOf('counts')}>
          <CountsScreen nav={nav} />
        </NavResetContext.Provider>
      ) : null}
      {current === 'vendors' && canPurchase ? (
        <NavResetContext.Provider value={resetOf('vendors')}>
          <VendorsScreen nav={nav} />
        </NavResetContext.Provider>
      ) : null}
    </>
  );
}
